import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * Botones de Novedades con las medidas del `ActionButton` del ATS
 * (hrbf_freezer/src/ui/components/ActionButton.tsx): 40 px de alto, radio 8, padding 10/14,
 * letra de 14 px y peso 400, borde de 1,5 px y sin sombra. Los colores salen del tema de cooler.
 * Solo para esta sección: el tema global de cooler no cambia.
 */
const base = {
    minHeight: 40,
    height: 40,
    borderRadius: '8px',
    px: '14px',
    py: '10px',
    fontSize: '14px',
    fontWeight: 400,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
};

/**
 * Deshabilitado, con los colores de `DISABLED_STYLES` de freezer (neutral 100/200/400). El tema
 * global de cooler pinta `containedPrimary` con un degradado y texto blanco `!important`, que
 * pisan el gris de MUI: sin el `!important` de aquí, un botón deshabilitado se vería azul.
 */
const disabledSx = {
    '&.Mui-disabled': {
        border: '1.5px solid #E5E5E5 !important',
        background: '#F5F5F5 !important',
        color: '#A1A1A1 !important',
    },
};

/** Principal: fondo primario (variante `filled` de freezer). Usar con `variant="contained"`. */
export const filledButtonSx = { ...base, border: '1.5px solid transparent', ...disabledSx };

/** Secundario: borde gris y texto secundario (variante `outlined` de freezer). */
export const outlinedButtonSx = (theme) => ({
    ...base,
    border: `1.5px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
        boxShadow: 'none',
        border: `1.5px solid ${theme.palette.text.disabled}`,
        backgroundColor: theme.palette.action.hover,
    },
});

/** Destructivo: borde y texto de error (variante `outlined` + `error` de freezer). */
export const errorOutlinedButtonSx = (theme) => ({
    ...base,
    border: `1.5px solid ${theme.palette.error.main}`,
    color: theme.palette.error.dark,
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
        boxShadow: 'none',
        border: `1.5px solid ${theme.palette.error.dark}`,
        backgroundColor: alpha(theme.palette.error.main, 0.08),
    },
});

/**
 * Contenido de un botón con estado de carga: mientras `loading`, el texto (y el icono) se ocultan
 * con `visibility` —siguen ocupando su sitio, así que el botón no cambia de ancho— y encima va un
 * spinner del tamaño de la letra. El botón debe llevar `aria-label`: con el texto oculto, su nombre
 * accesible pasaría a ser el del spinner.
 */
export const LoadingLabel = ({ loading, children }) => (
    <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', visibility: loading ? 'hidden' : 'visible' }}
        >
            {children}
        </Box>
        {loading && (
            <CircularProgress
                size="1em"
                thickness={5}
                color="inherit"
                aria-label="Cargando"
                sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-0.5em', ml: '-0.5em' }}
            />
        )}
    </Box>
);
